import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../../../../Constant';
import { GlobalLoading, showLoading } from 'react-global-loading';
import { toast, ToastContainer } from 'react-toastify';
import messages from '../../../../../assets/Messages.json';
import asiTitle from '../../../../../asi.json';
import 'react-toastify/dist/ReactToastify.css';

// API configuration
const getAuthConfig = () => {
  let token = localStorage.getItem('jwt') ||
    localStorage.getItem('token') ||
    localStorage.getItem('authToken') ||
    localStorage.getItem('accessToken') ||
    localStorage.getItem('userToken');

  if (!token) {
    console.error('No token found in localStorage');
    return null;
  }

  return {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Accept': 'application/json',
      'Cache-Control': 'no-cache'
    }
  };
};

const BlockMAndNReport = () => {
  const navigate = useNavigate();

  // State for report data
  const [formData, setFormData] = useState({
    ssoName: '',
    ssoSignature: '',
    scrutinisingOfficerName: '',
    scrutinisingOfficerSignature: '',
    dateOfReceipt: '',
    dateOfVerification: '',
    dateOfSubmission: '',
    dateOfScrutiny: '',
    dateOfDespatch: '',
    comments: ''
  });

  const [loading, setLoading] = useState(true);
  const [storedLanguage, setStoredLanguage] = useState('en'); // Default to English

  // Memoized block info from localStorage
  const blockInfo = useMemo(() => {
    const balanceSheetData = localStorage.getItem('balanceSheetFormData');
    if (!balanceSheetData) {
      return {
        dslNo: '',
        surveyYear: '',
        nameUnit: '',
        pslNo: ''
      };
    }

    try {
      const parsedData = JSON.parse(balanceSheetData);
      return {
        dslNo: parsedData.dslNo || parsedData.despatchSlNo || '',
        surveyYear: parsedData.surveyYear || parsedData.year || '',
        nameUnit: parsedData.nameUnit || parsedData.companyName || '',
        pslNo: parsedData.pslNo || parsedData.permanentSlNo || ''
      };
    } catch (error) {
      console.error('Error parsing balanceSheetFormData:', error);
      return {
        dslNo: '',
        surveyYear: '',
        nameUnit: '',
        pslNo: ''
      };
    }
  }, []);

  // Fetch data function
  const fetchBlockMAndNData = useCallback(async () => {
    if (!blockInfo.dslNo || !blockInfo.surveyYear) {
      console.log('Missing DSL No or Survey Year');
      setLoading(false);
      return;
    }

    const requestPayload = {
      dsl_no: blockInfo.dslNo,
      survey_year: blockInfo.surveyYear.split('-')[0] // Extract year from "2022-23" -> "2022"
    };

    showLoading(true);
    setLoading(true);

    try {
      const config = getAuthConfig();
      if (!config) {
        toast.error('Authentication token not found. Please login again.', messages.COLOR_RED);
        setLoading(false);
        return;
      }

      const response = await axios.post(`${API_URL}api/asi/blockMAndNForm`, requestPayload, config);
      
      // Check if response is HTML (error page)
      if (typeof response.data === 'string' && response.data.trim().startsWith('<!DOCTYPE html>')) {
        console.error('Received HTML response instead of JSON:', response.data.substring(0, 200));
        toast.error('Invalid response from server. Please try again.', messages.COLOR_RED);
        setLoading(false);
        return;
      }

      // Check response structure
      if (response.data && typeof response.data === 'object') {
        const { status, data } = response.data;

        if (status && data && Array.isArray(data) && data.length > 0) {
          // Find Block M and Block N data from array
          const blockMData = data.find(item => item.block === 'M') || {};
          const blockNData = data.find(item => item.block === 'N') || {};

          // Helper function to format date from ISO string to readable format
          const formatDate = (dateString) => {
            if (!dateString) return '';
            try {
              const date = new Date(dateString);
              if (isNaN(date.getTime())) return dateString;
              // Format as DD-MM-YYYY
              const day = String(date.getDate()).padStart(2, '0');
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const year = date.getFullYear();
              return `${day}-${month}-${year}`;
            } catch (error) {
              return dateString;
            }
          };

          // Helper function to get full signature URL
          const getSignatureUrl = (filename, baseUrl) => {
            if (!filename || !baseUrl) return '';
            // Remove trailing slash from baseUrl if present
            const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
            return `${cleanBaseUrl}/${filename}`;
          };

          // Map API data to form fields
          setFormData({
            // Block M fields
            ssoName: blockMData.name_of_superintending_officer || '',
            ssoSignature: blockMData.signature_of_superintending_officer 
              ? getSignatureUrl(blockMData.signature_of_superintending_officer, blockMData.baseUrlofUploadfile)
              : '',
            scrutinisingOfficerName: blockMData.name_and_designation_of_scrutinising_officer || '',
            scrutinisingOfficerSignature: blockMData.signature_of_scrutinising_officer
              ? getSignatureUrl(blockMData.signature_of_scrutinising_officer, blockMData.baseUrlofUploadfile)
              : '',
            dateOfReceipt: formatDate(blockMData.date_of_receipt_from_factory),
            dateOfVerification: formatDate(blockMData.date_of_verification_compilation),
            dateOfSubmission: '', // Not in API response
            dateOfScrutiny: formatDate(blockMData.dates_of_scrutiny),
            dateOfDespatch: formatDate(blockMData.date_of_despatch),
            // Block N field
            comments: blockNData.comments_of_superintending_officer || ''
          });
          console.log('Block M and N data loaded successfully');
        } else {
          toast.warning('No data found for this DSL No. and Survey Year.', messages.COLOR_RED);
        }
      } else {
        console.error('Unexpected response structure:', response.data);
        toast.error('Invalid response format from server.', messages.COLOR_RED);
      }
    } catch (error) {
      console.error('API Error:', error);

      // Check if error response contains HTML
      if (error.response?.data && typeof error.response.data === 'string' && error.response.data.trim().startsWith('<!DOCTYPE html>')) {
        console.error('Server returned HTML error page:', error.response.data.substring(0, 200));
        toast.error('Server error: Received HTML instead of JSON. Please check API endpoint.', messages.COLOR_RED);
      } else if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.', messages.COLOR_RED);
      } else if (error.response?.status === 404) {
        toast.warning('No data found for this DSL No. and Survey Year.', messages.COLOR_RED);
      } else if (error.response?.data) {
        const errorMessage = error.response.data.message || error.response.data.error || 'Unknown error occurred';
        toast.error(`Error loading data: ${errorMessage}`, messages.COLOR_RED);
      } else {
        toast.error(`Error loading data: ${error.message}`, messages.COLOR_RED);
      }
    } finally {
      showLoading(false);
      setLoading(false);
    }
  }, [blockInfo.dslNo, blockInfo.surveyYear]);

  useEffect(() => {
    fetchBlockMAndNData();
  }, [fetchBlockMAndNData]);

  useEffect(() => {
    const storedLang = localStorage.getItem('language');
    if (storedLang) {
      setStoredLanguage(storedLang);
    }
  }, []);

  const handlePrint = () => {
    window.print();
  };

  const handleCancel = () => {
    navigate('/asiScheduleMainMenu');
  };

  return (
    <>
      <GlobalLoading />
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="light" />

      <style>
        {`
          .report-container {
            background-color: #fff;
            border: 1px solid #ddd;
            border-radius: 6px;
            padding: 20px;
            margin-bottom: 20px;
          }
          .report-title {
            background-color: #fff;
            color: #333;
            font-size: 18px;
            font-weight: bold;
            padding: 15px 20px;
            margin: -20px -20px 20px -20px;
            border-radius: 6px 6px 0 0;
            border-bottom: 2px solid #333;
            text-align: center;
          }
          .report-info {
            background-color: #f8f9fa;
            padding: 15px;
            margin-bottom: 20px;
            border-radius: 4px;
          }
          .header-row {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 30px;
            padding: 12px 15px;
            background-color: #f8f9fa;
            border-left: 4px solid #333;
            align-items: center;
          }
          .header-item {
            display: flex;
            align-items: center;
            gap: 10px;
          }
          .header-label {
            font-weight: 700;
            color: #333;
            font-size: 13px;
            white-space: nowrap;
          }
          .header-value {
            font-weight: 600;
            color: #555;
            font-size: 13px;
            word-break: break-word;
          }
          .form-section {
            margin-bottom: 25px;
            border: 1px solid #ddd;
            padding: 15px;
            border-radius: 4px;
            background-color: #f8f9fa;
          }
          .form-section-title {
            font-size: 13px;
            font-weight: 600;
            color: #333;
            margin-bottom: 15px;
            border-bottom: 1px solid #ddd;
            padding-bottom: 8px;
          }
          .form-row {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 15px;
            margin-bottom: 10px;
          }
          .form-group {
            display: flex;
            flex-direction: column;
          }
          .form-group label {
            font-weight: 600;
            color: #333;
            margin-bottom: 5px;
            font-size: 12px;
          }
          .form-group input,
          .form-group textarea {
            padding: 8px 10px;
            border: 1px solid #ddd;
            border-radius: 3px;
            font-size: 12px;
            background-color: #fff;
            transition: border-color 0.3s;
          }
          .form-group input:focus,
          .form-group textarea:focus {
            outline: none;
            border-color: #333;
            box-shadow: 0 0 3px rgba(51, 51, 51, 0.2);
          }
          .form-group textarea {
            resize: vertical;
            min-height: 250px;
          }
          .signature-image {
            max-width: 200px;
            max-height: 100px;
            border: 1px solid #ddd;
            border-radius: 4px;
            object-fit: contain;
            background-color: #fff;
            padding: 5px;
          }
          .signature-image:hover {
            border-color: #1976d2;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          }
          .no-data {
            text-align: center;
            color: #666;
            font-style: italic;
            padding: 40px;
            background-color: #f8f9fa;
            border-radius: 4px;
          }
          
        
          
          @media print {
            .no-print { display: none !important; }
            .report-container { border: none; margin: 0; padding: 0; }
            .report-title { margin: 0; border-radius: 0; }
            .report-info { margin: 0 0 20px 0; }
            .btn_orange { display: none !important; }
          }
          @media (max-width: 768px) {
            .header-row {
              grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
              gap: 10px;
            }
          }
        `}
      </style>

      <div className="container-fluid py-3">
        {/* Report Content */}
        <div className="report-container">
          <h4 className="page_heading border-bottom-0">
            <div dangerouslySetInnerHTML={{ __html: storedLanguage === 'hi' ? asiTitle.hi.BLOCK_M_TITLE : asiTitle.en.BLOCK_M_TITLE }} />
          </h4>
          <div className="report-info">
            <div className="header-row">
              <div className="header-item">
                <span className="header-label">DSL No:</span>
                <span className="header-value">{blockInfo.dslNo || 'Not Available'}</span>
              </div>
              <div className="header-item">
                <span className="header-label">Survey Year:</span>
                <span className="header-value">{blockInfo.surveyYear || 'Not Available'}</span>
              </div>
              <div className="header-item">
                <span className="header-label">PSL No:</span>
                <span className="header-value">{blockInfo.pslNo || 'Not Available'}</span>
              </div>
              <div className="header-item">
                <span className="header-label">Company:</span>
                <span className="header-value">{blockInfo.nameUnit || 'Not Available'}</span>
              </div>
            </div>
          </div>

          {!loading ? (
            <>
              {/* S.S.O. Information Section */}
              <div className="form-section">
                <div className="form-section-title">1. S.S.O. (Supervisor/Sub Sampler Officer) Information</div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Name of S.S.O.</label>
                    <input
                      type="text"
                      value={formData.ssoName}
                      readOnly
                    />
                  </div>
                  <div className="form-group">
                    <label>Signature of S.S.O.</label>
                    {formData.ssoSignature ? (
                      <img
                        src={formData.ssoSignature}
                        alt="S.S.O. Signature"
                        className="signature-image"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          if (e.target.nextSibling) {
                            e.target.nextSibling.style.display = 'block';
                          }
                        }}
                      />
                    ) : (
                      <input
                        type="text"
                        value=""
                        readOnly
                        placeholder="No signature available"
                      />
                    )}
                    {formData.ssoSignature && (
                      <div className="image-error-message" style={{ display: 'none', color: '#999', fontSize: '12px', marginTop: '5px' }}>
                        Signature image not available
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Scrutinising Officer Information Section */}
              <div className="form-section">
                <div className="form-section-title">2. Scrutinising Officer Information</div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Name and Designation of Scrutinising Officer</label>
                    <input
                      type="text"
                      value={formData.scrutinisingOfficerName}
                      readOnly
                    />
                  </div>
                  <div className="form-group">
                    <label>Signature of Scrutinising Officer</label>
                    {formData.scrutinisingOfficerSignature ? (
                      <img
                        src={formData.scrutinisingOfficerSignature}
                        alt="Scrutinising Officer Signature"
                        className="signature-image"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          if (e.target.nextSibling) {
                            e.target.nextSibling.style.display = 'block';
                          }
                        }}
                      />
                    ) : (
                      <input
                        type="text"
                        value=""
                        readOnly
                        placeholder="No signature available"
                      />
                    )}
                    {formData.scrutinisingOfficerSignature && (
                      <div className="image-error-message" style={{ display: 'none', color: '#999', fontSize: '12px', marginTop: '5px' }}>
                        Signature image not available
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Important Dates Section */}
              <div className="form-section">
                <div className="form-section-title">3. Important Dates</div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Date of Receipt from Factory</label>
                    <input
                      type="text"
                      value={formData.dateOfReceipt || ''}
                      readOnly
                    />
                  </div>
                  <div className="form-group">
                    <label>Date of Verification/Compilation</label>
                    <input
                      type="text"
                      value={formData.dateOfVerification || ''}
                      readOnly
                    />
                  </div>
                  <div className="form-group">
                    <label>Date of Submission by S.S.O.</label>
                    <input
                      type="text"
                      value={formData.dateOfSubmission || ''}
                      readOnly
                    />
                  </div>
                  <div className="form-group">
                    <label>Date(s) of Scrutiny</label>
                    <input
                      type="text"
                      value={formData.dateOfScrutiny || ''}
                      readOnly
                    />
                  </div>
                  <div className="form-group">
                    <label>Date of Despatch</label>
                    <input
                      type="text"
                      value={formData.dateOfDespatch || ''}
                      readOnly
                    />
                  </div>
                </div>
              </div>

              {/* Comments Section */}
              <div className="form-section">
                <div className="form-section-title">4. COMMENTS of Supdt./Sr.Supdt./Scrutinising officer(Please note that reasons for abnormal values or entries (high or low)in respect of important characteristics (such as GVA, working capital, wage rate, number of workers, distributive expenses, depreciation etc.) should be given invariably. for instance reason for high GVA could be increase demand/production, profit, govt. subsidy; or for low GVA, the reasons could be decreased demand/production, capacity under/utilisation, high input cost etc. Similarly, reasons for abnormal values of certain important ratios e.g. ratio of output to input, ratio of depreciation/distributive expenses to output etc. should be given. Please refer detailed instructions also for further guidance.</div>
                <div className="form-group">
                  <textarea
                    value={formData.comments}
                    readOnly
                  />
                </div>
              </div>
            </>
          ) : (
            <div className="no-data">Loading Block M and N data...</div>
          )}
        </div>

        {/* Action Buttons */}
        {/* <div className="row no-print">
          <div className="col-12">
            <hr />
          </div>
          <div className="col-md-8 text-center text-md-start mb-3 mb-md-0">
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={handlePrint}
            >
              Print
              <i className="fa fa-print ms-1"></i>
            </button>
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={handleCancel}
            >
              Cancel
            </button>
          </div>
          <div className="col-md-4 text-center text-md-end">
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => navigate(-1)}
            >
              <i className="fa fa-angle-left me-1"></i>
              Previous
            </button>
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => navigate('/reports')}
            >
              Next
              <i className="fa fa-angle-right ms-1"></i>
            </button>
          </div>
        </div> */}


        <div className="row no-print">
          <div className="col-12">
            <hr />
          </div>
          <div className="col-md-8 text-center text-md-start mb-3 mb-md-0">
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => window.print()}
            >
              Print
              <i className="fa fa-print ms-1"></i>
            </button>
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => navigate('/asiScheduleMainMenu')}

            >
              Cancel
            </button>
          </div>
          <div className="col-md-4 text-center text-md-end">
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => navigate(-1)}
            >
              <i className="fa fa-angle-left me-1"></i>
              Previous
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default BlockMAndNReport;